import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import './EventDetails.css';
import { translateText, detectLanguage } from './translationUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventDetails = ({ event, categories, language = 'ru', onClose, onBack, onShowOnMap }) => {
  const { user } = useAuth();
  const [eventData, setEventData] = useState(event);
  const [loading, setLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  const t = {
    kk: {
      backToFeed: "Таспаға оралу",
      eventDetails: "Оқиға туралы толық ақпарат",
      description: "Сипаттама",
      address: "Мекенжай",
      coordinates: "Координаттар",
      status: "Мәртебе",
      priority: "Маңыздылық", 
      assignedTo: "Тағайындалған",
      createdBy: "Жасаған",
      createdAt: "Жасалған күні",
      updatedAt: "Жаңартылған",
      photos: "Фотосуреттер",
      comments: "Пікірлер",
      addComment: "Пікір қосу",
      submitComment: "Жіберу",
      noPhotos: "Фотосуреттер жоқ",
      noComments: "Пікірлер жоқ",
      commentPlaceholder: "Пікіріңізді жазыңыз...",
      loadingComment: "Жіберілуде..."
    },
    ru: {
      backToFeed: "Назад к ленте",
      eventDetails: "Подробная информация о событии",
      description: "Описание",
      address: "Адрес",
      coordinates: "Координаты",
      status: "Статус",
      priority: "Приоритет",
      assignedTo: "Назначено",
      createdBy: "Создал",
      createdAt: "Создано",
      updatedAt: "Обновлено",
      photos: "Фотографии",
      comments: "Комментарии",
      addComment: "Добавить комментарий",
      submitComment: "Отправить",
      noPhotos: "Нет фотографий",
      noComments: "Нет комментариев",
      commentPlaceholder: "Напишите ваш комментарий...",
      loadingComment: "Отправка..."
    }
  };

  const currentT = t[language] || t.ru;

  const getCategoryData = (categoryId) => {
    // Маппинг бизнес-категорий
    const businessCategories = {
      'business_opening': {
        name_ru: 'Открытие магазина',
        name_kk: 'Дүкеннің ашылуы',
        color: '#9B59B6',
        icon: '🏪'
      },
      'business_sale': {
        name_ru: 'Распродажа/Скидки',
        name_kk: 'Жеңілдіктер',
        color: '#E74C3C',
        icon: '🏷️'
      },
      'business_fair': {
        name_ru: 'Ярмарка',
        name_kk: 'Жәрмеңке',
        color: '#F39C12',
        icon: '🎪'
      },
      'business_event': {
        name_ru: 'Мероприятие/Акция',
        name_kk: 'Іс-шара',
        color: '#3498DB',
        icon: '🎉'
      },
      'business_cafe': {
        name_ru: 'Новое кафе/ресторан',
        name_kk: 'Жаңа кафе/мейрамхана',
        color: '#E67E22',
        icon: '🍽️'
      },
      'business_service': {
        name_ru: 'Услуги',
        name_kk: 'Қызметтер',
        color: '#1ABC9C',
        icon: '🔧'
      },
      'business_vacancy': {
        name_ru: 'Требуются',
        name_kk: 'Қажет',
        color: '#2ECC71',
        icon: '💼'
      }
    };
    
    // Проверяем сначала бизнес-категории
    if (businessCategories[categoryId]) {
      return businessCategories[categoryId];
    }
    
    // Иначе ищем в обычных категориях
    return categories.find(cat => cat.id === categoryId) || {
      name_ru: categoryId,
      name_kk: categoryId,
      color: '#6b7c6e',
      icon: '📍'
    };
  };

  const getStatusText = (status) => {
    const statusMap = {
      kk: {
        new: "Жаңа",
        assigned: "Тағайындалған",
        in_progress: "Орындалуда",
        completed: "Аяқталған",
        final_completed: "Толық аяқталған",
        cancelled: "Тоқтатылған",
        rejected: "Қабылданбаған"
      },
      ru: {
        new: "Новое",
        assigned: "Назначено",
        in_progress: "В работе",
        completed: "Завершено",
        final_completed: "Полностью завершено",
        cancelled: "Отменено",
        rejected: "Отклонено"
      }
    };
    return statusMap[language]?.[status] || status;
  };

  const getServiceName = (serviceType, lang) => {
    const serviceNames = {
      emergency_service: { kk: 'Төтенше жағдайлар', ru: 'ЧС' },
      municipal_service: { kk: 'Коммуналдық шаруашылық бөлімі', ru: 'Отдел ЖКХ' },
      architecture_service: { kk: 'Сәулет бөлімі', ru: 'Отдел Архитектуры' },
      heating_service: { kk: 'Жылу орталығы', ru: 'Теплоцентраль' },
      electricity_service: { kk: 'ОЭСК', ru: 'ОЭСК' },
      telecom_service: { kk: 'Қазақтелеком', ru: 'Казахтелеком' },
      traffic_police_service: { kk: 'ГАИ', ru: 'ГАИ' },
      police_service: { kk: 'Полиция', ru: 'Полиция' },
      improvement_service: { kk: 'Зырян Тәртіп', ru: 'Зырян Тәртіп' },
      education_service: { kk: 'ГОРОНО', ru: 'ГОРОНО' },
      culture_sport_service: { kk: 'Мәдениет және спорт бөлімі', ru: 'Отдел Культуры и Спорта' },
      waste_service: { kk: 'ШемКомХоз', ru: 'ШемКомХоз' },
      healthcare_service: { kk: 'Денсаулық сақтау', ru: 'Горздрав' },
      lighting_service: { kk: 'Көше жарығы', ru: 'Уличное освещение' }
    };
    
    return serviceNames[serviceType]?.[lang] || (lang === 'kk' ? 'Қызмет' : 'Служба');
  };

  // Определяет службу по подстроке в assigned_to ID
  const getAssignedName = (assignedTo, lang) => {
    const id = (assignedTo || '').toLowerCase();
    const map = {
      emergency: 'emergency_service',
      municipal: 'municipal_service',
      improvement: 'improvement_service',
      architecture: 'architecture_service',
      heating: 'heating_service',
      electricity: 'electricity_service',
      telecom: 'telecom_service',
      traffic: 'traffic_police_service',
      police: 'police_service',
      education: 'education_service',
      culture: 'culture_sport_service',
      waste: 'waste_service',
      healthcare: 'healthcare_service',
      lighting: 'lighting_service'
    };
    for (const [key, serviceType] of Object.entries(map)) {
      if (id.includes(key)) return getServiceName(serviceType, lang);
    }
    return lang === 'kk' ? 'Қызмет' : 'Служба';
  };

  // Function to get auto-translated description
  const getTranslatedDescription = (eventData) => {
    let description = '';
    
    if (language === 'kk') {
      // If interface is Kazakh, prefer Kazakh description or translate Russian
      description = eventData.description_kk || eventData.description_ru || '';
      if (!eventData.description_kk && eventData.description_ru) {
        description = translateText(eventData.description_ru, 'ru', 'kk');
      }
    } else {
      // If interface is Russian, prefer Russian description or translate Kazakh  
      description = eventData.description_ru || eventData.description_kk || '';
      if (!eventData.description_ru && eventData.description_kk) {
        description = translateText(eventData.description_kk, 'kk', 'ru');
      }
    }
    
    return description;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString(language === 'kk' ? 'kk-KZ' : 'ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    
    if (!newComment.trim() || !user) return;

    setSubmittingComment(true);

    try {
      const response = await axios.post(
        `${API}/events/${eventData.id}/comments`,
        { comment: newComment.trim() }
      );

      // Update event data with new comment
      setEventData(prev => ({
        ...prev,
        comments: [...(prev.comments || []), response.data]
      }));

      setNewComment('');
    } catch (err) {
      console.error('Error adding comment:', err);
    } finally {
      setSubmittingComment(false);
    }
  };

  const categoryData = getCategoryData(eventData.category);
  const hasPhotos = eventData.photos && eventData.photos.length > 0;
  const hasComments = eventData.comments && eventData.comments.length > 0;

  // Function to get translated comment text based on current language
  const getTranslatedCommentText = (comment) => {
    const originalText = comment.comment_ru || comment.comment || '';
    if (!originalText) return '';

    // If user is viewing in Kazakh, auto-translate from Russian
    if (language === 'kk') {
      return translateText(originalText, 'ru', 'kk');
    }

    // If user is viewing in Russian, check if original is Kazakh and translate
    if (language === 'ru') {
      const detectedLang = detectLanguage(originalText);
      if (detectedLang === 'kk') {
        return translateText(originalText, 'kk', 'ru');
      }
    }

    return originalText;
  };

  return (
    <div className="event-details-overlay">
      <div className="event-details">
        <div className="event-details-header">
          <button className="back-btn" onClick={onBack}>
            ← {currentT.backToFeed}
          </button>
          <h2>{currentT.eventDetails}</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="event-details-content">
          {/* Event Header */}
          <div className={`event-header ${eventData.category === 'emergency' ? 'emergency' : ''}`}>
            <div className="event-category-large">
              <span 
                className="category-icon-large"
                style={{ backgroundColor: categoryData.color }}
              >
                {categoryData.icon}
              </span>
              <div className="category-info">
                <h3>{language === 'kk' ? categoryData.name_kk : categoryData.name_ru}</h3>
                <div className="event-status-large">
                  <span className={`status-badge status-${eventData.status}`}>
                    {getStatusText(eventData.status)}
                  </span>
                  {eventData.priority > 3 && (
                    <span className="priority-large">
                      {'⭐'.repeat(eventData.priority)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="detail-section">
            <h4>{currentT.description}</h4>
            <p className="event-description-full">
              {getTranslatedDescription(eventData)}
            </p>
          </div>

          {/* Location Info */}
          <div className="detail-section">
            <div className="location-grid">
              {eventData.address && (
                <div className="location-item">
                  <strong>{currentT.address}:</strong>
                  <span>{eventData.address}</span>
                </div>
              )}
              <div className="location-item coordinates-item">
                <strong>{currentT.coordinates}:</strong>
                <div className="coordinates-with-button">
                  <span>{eventData.location.lat.toFixed(6)}, {eventData.location.lng.toFixed(6)}</span>
                  <button 
                    className="show-on-map-btn"
                    onClick={() => onShowOnMap && onShowOnMap(eventData)}
                    title={language === 'kk' ? 'Картада көрсету' : 'Показать на карте'}
                  >
                    📍 {language === 'kk' ? 'Картада көрсету' : 'Показать на карте'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Event Meta */}
          <div className="detail-section">
            <div className="meta-grid">
              <div className="meta-item">
                <strong>{currentT.createdBy}:</strong>
                <span>{language === 'kk' ? 'Тұрғын' : 'Житель'} {eventData.created_by_user?.full_name ? eventData.created_by_user.full_name.split(' ')[0] : ''}</span>
              </div>
              <div className="meta-item">
                <strong>{currentT.createdAt}:</strong>
                <span>{formatDate(eventData.created_at)}</span>
              </div>
              {(eventData.assigned_to_user || eventData.assigned_to) && (
                <div className="meta-item">
                  <strong>{currentT.assignedTo}:</strong>
                  <span>{
                    eventData.assigned_to_user ? 
                      getServiceName(eventData.assigned_to_user.service_type, language) :
                      getAssignedName(eventData.assigned_to, language)
                  }</span>
                </div>
              )}
              {eventData.updated_at && eventData.updated_at !== eventData.created_at && (
                <div className="meta-item">
                  <strong>{currentT.updatedAt}:</strong>
                  <span>{formatDate(eventData.updated_at)}</span>
                </div>
              )}
              {user && (user.role === 'service' || user.role === 'admin') && eventData.reporter_full_name && (
                <div className="meta-item" data-testid="event-reporter-name">
                  <strong>{language === 'kk' ? 'Өтініш беруші' : 'ФИО заявителя'}:</strong>
                  <span>👤 {eventData.reporter_full_name}</span>
                </div>
              )}
              {user && (user.role === 'service' || user.role === 'admin') && eventData.reporter_phone && (
                <div className="meta-item" data-testid="event-reporter-phone">
                  <strong>{language === 'kk' ? 'Өтініш берушінің телефоны' : 'Телефон заявителя'}:</strong>
                  <span>
                    <a href={`tel:${eventData.reporter_phone}`} style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}>
                      📞 {eventData.reporter_phone}
                    </a>
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Photos */}
          <div className="detail-section">
            <h4>{currentT.photos}</h4>
            {hasPhotos ? (
              <div className="photos-grid">
                {eventData.photos.map((photo, index) => (
                  <div key={`photo-${eventData.id}-${index}`} className="photo-item">
                    <img
                      src={`${BACKEND_URL}${photo}`}
                      alt={`Фото события ${index + 1}`}
                      className="event-photo"
                      onClick={() => window.open(`${BACKEND_URL}${photo}`, '_blank')}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-content">{currentT.noPhotos}</p>
            )}
          </div>

          {/* Comments */}
          <div className="detail-section">
            <h4>{currentT.comments}</h4>
            
            {/* Add Comment Form */}
            {user && (
              <form onSubmit={handleAddComment} className="add-comment-form">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder={currentT.commentPlaceholder}
                  className="comment-input"
                  rows={3}
                  disabled={submittingComment}
                />
                <button
                  type="submit"
                  className="submit-comment-btn"
                  disabled={!newComment.trim() || submittingComment}
                >
                  {submittingComment ? currentT.loadingComment : currentT.submitComment}
                </button>
              </form>
            )}

            {/* Comments List */}
            {hasComments ? (
              <div className="comments-list">
                {eventData.comments.map((comment) => (
                  <div key={comment.id || `comment-${comment.created_at}`} className="comment-item">
                    <div className="comment-header">
                      <strong>
                        {(() => {
                          const userName = comment.user_name;
                          const serviceWord = language === 'kk' ? 'Қызмет' : 'Служба';
                          const residentWord = language === 'kk' ? 'Тұрғын' : 'Житель';
                          
                          // Если user_name пустой или undefined, пытаемся определить по тексту комментария
                          if (!userName || userName === 'Пользователь') {
                            if (comment.comment_ru?.includes('Принято') || 
                                comment.comment_ru?.includes('Выезжаем') || 
                                comment.comment_ru?.includes('В работе') ||
                                comment.comment_ru?.includes('Выполнено')) {
                              return serviceWord;
                            }
                            return residentWord;
                          }
                          
                          // Проверяем, является ли это службой по названию
                          const serviceNames = ['ЧС', 'ГАИ', 'ЖКХ', 'Полиция', 'Теплоцентраль', 'ОЭСК', 'Казахтелеком', 
                                              'ГОРОНО', 'Горздрав', 'ШемКомХоз', 'Архитектуры', 'Культуры', 'Зырян', 'освещение'];
                          
                          const isService = serviceNames.some(service => 
                            userName.includes(service) || userName.startsWith('Служба')
                          );
                          
                          if (isService) {
                            return userName.startsWith('Служба') ? userName : `${serviceWord} ${userName}`;
                          }
                          
                          // Если это житель
                          const firstName = userName.split(' ')[0];
                          return `${residentWord} ${firstName}`;
                        })()}
                      </strong>
                      <span className="comment-date">{formatDate(comment.created_at)}</span>
                    </div>
                    <p className="comment-text">{getTranslatedCommentText(comment)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-content">{currentT.noComments}</p>
            )}
          </div>

          {/* History section removed per user request */}
        </div>
      </div>
    </div>
  );
};

export default EventDetails;